import type { ConversationProposal, RecipeDiff } from '@recipes/contracts';
import { Badge, Button, Input, Label } from '@recipes/ui';
import { Minus, Pencil, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export type ApplyMode = 'APPLY_TO_RECIPE' | 'CREATE_VARIATION' | 'CREATE_NEW';

function DiffList({
  title,
  added,
  removed,
  changed,
}: {
  title: string;
  added: string[];
  removed: string[];
  changed: Array<{ before: string; after: string }>;
}) {
  const { t } = useTranslation();
  if (added.length + removed.length + changed.length === 0) return null;
  return (
    <div>
      <h4 className="mb-1 text-sm font-semibold">{title}</h4>
      <ul className="space-y-1 text-sm">
        {added.map((a, i) => (
          <li key={`a${i}`} className="flex gap-2 rounded bg-success/10 px-2 py-1">
            <Plus className="mt-0.5 size-4 shrink-0 text-success" aria-hidden />
            <span className="sr-only">{t('assistant.diff.added')}:</span>
            <span>{a}</span>
          </li>
        ))}
        {removed.map((r, i) => (
          <li key={`r${i}`} className="flex gap-2 rounded bg-destructive/10 px-2 py-1">
            <Minus className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
            <span className="sr-only">{t('assistant.diff.removed')}:</span>
            <span className="line-through decoration-destructive/60">{r}</span>
          </li>
        ))}
        {changed.map((c, i) => (
          <li key={`c${i}`} className="flex gap-2 rounded bg-warning/15 px-2 py-1">
            <Pencil className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span className="sr-only">{t('assistant.diff.changed')}:</span>
            <span>
              <span className="text-muted-foreground line-through">{c.before}</span>
              <span aria-hidden> → </span>
              <span className="sr-only">{t('assistant.diff.to')}</span>
              <span>{c.after}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Structured diff of an assistant proposal (spec §7.4: material changes shown before applying). */
export function RecipeDiffView({ diff }: { diff: RecipeDiff }) {
  const { t } = useTranslation();
  const scalar = diff.changes.filter((c) => c.path !== 'ingredients' && c.path !== 'steps');
  const empty = diff.changes.length === 0;
  return (
    <div className="space-y-3" data-testid="proposal-diff">
      {empty && <p className="text-sm text-muted-foreground">{t('assistant.diff.none')}</p>}
      {scalar.length > 0 && (
        <div>
          <h4 className="mb-1 text-sm font-semibold">{t('assistant.diff.fields')}</h4>
          <ul className="space-y-1 text-sm">
            {scalar.map((c, i) => (
              <li key={i} className="flex flex-wrap gap-2 rounded bg-muted px-2 py-1">
                <Badge variant="outline">{c.label}</Badge>
                <span className="text-muted-foreground">{t(`assistant.diff.${c.kind}`)}</span>
                {c.before !== undefined && c.before !== null && (
                  <span className="line-through">{String(c.before)}</span>
                )}
                {c.after !== undefined && c.after !== null && <span>{String(c.after)}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      <DiffList
        title={t('assistant.diff.ingredients')}
        added={diff.ingredientsAdded}
        removed={diff.ingredientsRemoved}
        changed={diff.ingredientsChanged}
      />
      <DiffList
        title={t('assistant.diff.steps')}
        added={diff.stepsAdded}
        removed={diff.stepsRemoved}
        changed={diff.stepsChanged}
      />
    </div>
  );
}

export function ProposalCard({
  proposal,
  hasRecipe,
  applying,
  onApply,
}: {
  proposal: ConversationProposal;
  hasRecipe: boolean;
  applying?: ApplyMode | null;
  onApply: (mode: ApplyMode, variationLabel?: string) => void;
}) {
  const { t } = useTranslation();
  const [label, setLabel] = useState('');
  const [showLabel, setShowLabel] = useState(false);
  const canModify = hasRecipe && proposal.kind === 'MODIFY_RECIPE' && proposal.proposedRecipe;
  const canCreate = Boolean(proposal.draft ?? proposal.proposedRecipe);

  if (proposal.kind === 'ANSWER') return null;

  return (
    <div className="mt-2 space-y-3 rounded-lg border bg-background p-3" data-testid="proposal-card">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="info">{t(`assistant.proposal.kinds.${proposal.kind}`)}</Badge>
        {proposal.applied && <Badge variant="success">{t('assistant.proposal.applied')}</Badge>}
      </div>
      <p className="text-sm">{proposal.summary}</p>
      {proposal.diff && <RecipeDiffView diff={proposal.diff} />}
      {proposal.draft && !proposal.diff && (
        <div className="text-sm">
          <p className="font-medium">{proposal.draft.title}</p>
          <p className="text-muted-foreground">
            {t('assistant.proposal.draftSummary', {
              ingredients: proposal.draft.ingredients.length,
              steps: proposal.draft.steps.length,
            })}
          </p>
        </div>
      )}
      {!proposal.applied && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            {canModify && (
              <Button
                size="sm"
                loading={applying === 'APPLY_TO_RECIPE'}
                disabled={Boolean(applying)}
                onClick={() => onApply('APPLY_TO_RECIPE')}
              >
                {t('assistant.proposal.apply')}
              </Button>
            )}
            {hasRecipe && canCreate && (
              <Button
                size="sm"
                variant="outline"
                aria-expanded={showLabel}
                disabled={Boolean(applying)}
                onClick={() => setShowLabel((s) => !s)}
              >
                {t('assistant.proposal.variation')}
              </Button>
            )}
            {canCreate && (
              <Button
                size="sm"
                variant="outline"
                loading={applying === 'CREATE_NEW'}
                disabled={Boolean(applying)}
                onClick={() => onApply('CREATE_NEW')}
              >
                {t('assistant.proposal.createNew')}
              </Button>
            )}
          </div>
          {showLabel && (
            <form
              className="flex flex-wrap items-end gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                onApply('CREATE_VARIATION', label.trim() || undefined);
              }}
            >
              <div>
                <Label htmlFor="variation-label">{t('assistant.proposal.variationLabel')}</Label>
                <Input
                  id="variation-label"
                  className="mt-1"
                  maxLength={80}
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder={t('assistant.proposal.variationPlaceholder')}
                />
              </div>
              <Button
                type="submit"
                size="sm"
                loading={applying === 'CREATE_VARIATION'}
                disabled={Boolean(applying)}
              >
                {t('assistant.proposal.createVariation')}
              </Button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
